import { useAuth0 } from "@auth0/auth0-react";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import SportsEsportsRoundedIcon from "@mui/icons-material/SportsEsportsRounded";
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  CssBaseline,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  ThemeProvider,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import FeedbackButton from "../components/feedback/FeedbackButton";
import { useDarkMode } from "../hooks/useDarkMode/useDarkMode";
import { useUsers } from "../hooks/useUsers/useUsers";
import Avatar from "../profile/Avatar";
import { darkTheme, lightTheme } from "../theme/theme";

type DesktopNavItem = {
  label: string;
  path: string;
  requiresAuth?: boolean;
};

const LAST_LEAGUE_KEY = "pickem.lastLeague";
const DASHBOARD_LEAGUE_KEY = "pickem.dashboard.league";

const ResponsiveShell = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
  const [isDarkMode, toggleDarkMode] = useDarkMode();
  const isDesktop = useMediaQuery("(min-width:900px)");
  const location = useLocation();
  const navigate = useNavigate();
  const { createUser } = useUsers();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const isLeagueRoute = /^\/leagues\/[^/]+(?:\/|$)/.test(location.pathname);

  useEffect(() => {
    if (!isAuthenticated) return;
    void createUser()
      .then((created) => {
        if (!created) console.error("Failed to create user");
      })
      .catch((error) => {
        console.error("Failed to create user", error);
      });
  }, [createUser, isAuthenticated]);

  useEffect(() => {
    const match = location.pathname.match(/^\/leagues\/([^/]+)/);
    if (match?.[1]) localStorage.setItem(LAST_LEAGUE_KEY, match[1]);
  }, [location.pathname]);

  const desktopNavigation = useMemo<DesktopNavItem[]>(
    () => [
      { label: "Home", path: "/" },
      { label: "Dashboard", path: "/dashboard", requiresAuth: true },
      { label: "Leagues", path: "/leagues", requiresAuth: true },
      { label: "Profile", path: "/profile", requiresAuth: true },
    ],
    []
  );

  const visibleDesktopNavigation = desktopNavigation.filter(
    (item) => !item.requiresAuth || isAuthenticated
  );

  const activeDesktopPath =
    visibleDesktopNavigation.find(
      (item) => item.path !== "/" && location.pathname.startsWith(item.path)
    )?.path || (location.pathname === "/" ? "/" : false);

  const rememberedLeague =
    localStorage.getItem(LAST_LEAGUE_KEY) ||
    localStorage.getItem(DASHBOARD_LEAGUE_KEY);

  const mobileItems = [
    { label: "Dashboard", value: "/dashboard", icon: <DashboardRoundedIcon /> },
    { label: "Picks", value: "picks", icon: <SportsEsportsRoundedIcon /> },
    { label: "Leagues", value: "/leagues", icon: <GroupsRoundedIcon /> },
  ];

  const activeMobilePath = location.pathname.startsWith("/dashboard")
    ? "/dashboard"
    : location.pathname.startsWith("/leagues/")
    ? "picks"
    : location.pathname.startsWith("/leagues")
    ? "/leagues"
    : false;

  const signIn = () =>
    loginWithRedirect({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: "openid profile email offline_access",
      },
      appState: {
        returnTo:
          location.pathname === "/"
            ? "/dashboard"
            : `${location.pathname}${location.search}`,
      },
    });

  const handleMobileNavigation = (value: string) => {
    if (value === "picks") {
      navigate(rememberedLeague ? "/leagues/" + rememberedLeague + "/picks" : "/leagues");
      return;
    }
    navigate(value);
  };

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline enableColorScheme />
      <Box sx={{ minHeight: "100dvh", bgcolor: "background.default", color: "text.primary" }}>
        <AppBar position="fixed" color="default" elevation={0}>
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, md: 72 },
              px: { xs: 2, md: "60px" },
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr auto",
                md: "minmax(230px, 1fr) auto minmax(230px, 1fr)",
              },
              alignItems: "center",
            }}
          >
            <Button
              component={Link}
              to="/"
              color="inherit"
              aria-label="Pickem home"
              sx={{
                justifySelf: "start",
                gap: 1.25,
                minWidth: 0,
                minHeight: 44,
                px: 0,
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: "8px",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 900,
                }}
              >
                P
              </Box>
              <Typography
                component="span"
                sx={{
                  display: { xs: "none", sm: "block" },
                  color: "text.primary",
                  fontSize: 20,
                  lineHeight: "26px",
                  fontWeight: 800,
                }}
              >
                Pickem
              </Typography>
            </Button>

            {isDesktop && (
              <Box
                component="nav"
                aria-label="Primary navigation"
                sx={{ gridColumn: 2, display: "flex", justifySelf: "center", gap: "6px" }}
              >
                {visibleDesktopNavigation.map((item) => {
                  const selected = activeDesktopPath === item.path;
                  return (
                    <Button
                      key={item.path}
                      component={Link}
                      to={item.path}
                      color={selected ? "primary" : "inherit"}
                      variant={selected ? "contained" : "text"}
                      aria-current={selected ? "page" : undefined}
                      sx={{ minHeight: 48, px: 2, borderRadius: 0, boxShadow: "none" }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifySelf: "end",
                gap: 1,
                gridColumn: { xs: 2, md: 3 },
              }}
            >
              {isDesktop && (
                <FeedbackButton
                  variant="button"
                  size="medium"
                  color="inherit"
                  sx={{
                    minWidth: 94,
                    border: 0,
                    px: 1,
                    "& .MuiButton-startIcon": { display: "none" },
                  }}
                />
              )}
              <Button
                onClick={toggleDarkMode}
                color="inherit"
                aria-label={isDarkMode ? "Use light theme" : "Use dark theme"}
                startIcon={
                  isDarkMode ? (
                    <LightModeOutlinedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <DarkModeOutlinedIcon sx={{ fontSize: 18 }} />
                  )
                }
                sx={{
                  minWidth: { xs: 44, md: 76 },
                  minHeight: 40,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 20,
                  px: { xs: 1, md: 1.25 },
                  "& .MuiButton-startIcon": { mr: { xs: 0, md: 0.75 } },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                  {isDarkMode ? "Light" : "Dark"}
                </Box>
              </Button>
              {isAuthenticated ? (
                <>
                  <IconButton
                    onClick={(event) => setMenuAnchor(event.currentTarget)}
                    aria-label="Open account menu"
                    sx={{ p: 0.5 }}
                  >
                    <Avatar
                      email={user?.email}
                      auth0Picture={user?.picture}
                      alt={user?.name || user?.email || "Account"}
                      sx={{ width: 40, height: 40 }}
                    />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                  >
                    <MenuItem disabled>{user?.email}</MenuItem>
                    <Divider />
                    <MenuItem component={Link} to="/profile" onClick={() => setMenuAnchor(null)}>
                      <PersonRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                      Profile
                    </MenuItem>
                    {!isDesktop && <FeedbackButton variant="list-item" />}
                    <MenuItem
                      onClick={() =>
                        logout({ logoutParams: { returnTo: window.location.origin } })
                      }
                    >
                      <LogoutRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                      Sign out
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button variant="contained" startIcon={<LoginRoundedIcon />} onClick={signIn}>
                  Sign in
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            minHeight: "100dvh",
            pt: { xs: "64px", md: "72px" },
            pb:
              !isDesktop && !isLeagueRoute && isAuthenticated
                ? "calc(72px + env(safe-area-inset-bottom, 0px))"
                : 0,
          }}
        >
          {children}
        </Box>

        {!isDesktop && !isLeagueRoute && isAuthenticated && (
          <Paper
            component="nav"
            aria-label="Primary mobile navigation"
            square
            elevation={8}
            sx={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: (theme) => theme.zIndex.appBar,
              pb: "env(safe-area-inset-bottom, 0px)",
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            <BottomNavigation
              value={activeMobilePath}
              onChange={(_, value) => handleMobileNavigation(value)}
              showLabels
            >
              {mobileItems.map((item) => (
                <BottomNavigationAction
                  key={item.value}
                  value={item.value}
                  label={item.label}
                  icon={item.icon}
                />
              ))}
            </BottomNavigation>
          </Paper>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default ResponsiveShell;
